import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, InputAdornment, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getReferenceTableById } from "../../../api/reference";
import { getVariables } from "../../../api/variables";
import { useReferenceData } from "../../../hooks";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { DepthMeasurementRecordForm as DepthMeasurementRecordFormType, getDepthMeasurementRecordFormSchema } from "../../../types/forms/capture";
import { DepthMeasurement } from "../../../types/model/depthMeasurement";
import { VariableResponse } from "../../../types/model/variable";
import { VARIABLE_CATEGORY_CODES } from "../../../types/model/variable/VariableCategories";
import FormAutocomplete from "../../General/FormAutocomplete";

interface DepthMeasurementFormProps {
  depthMeasurementData?: DepthMeasurement;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: DepthMeasurementRecordFormType) => Promise<void>;
}

export default function DepthMeasurementForm({
  depthMeasurementData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<DepthMeasurementFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getDepthMeasurementRecordFormSchema(t);
  const methods = useForm<DepthMeasurementRecordFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      startDepth : depthMeasurementData?.startDepth ?? undefined,
      endDepth: depthMeasurementData?.endDepth ?? undefined,
      variableId: depthMeasurementData?.variableId ?? undefined,
      value: depthMeasurementData?.value ?? undefined,
      flag: depthMeasurementData?.flag ?? undefined,
      measuredAt: depthMeasurementData?.measuredAt ?? null
    },
  });

  const [variables, setVariables] = useState<VariableResponse[]>([]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await getVariables(
          false,
          VARIABLE_CATEGORY_CODES.DEPTH_MEASUREMENT_RECORD,
        );
        setVariables(data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchVariables();
  }, []);
  
  const variableId = methods.watch("variableId");
  const selectedVariable = variables.find((v: any) => v.id === variableId);
  const [lookupTableInfo, setLookupTableInfo] = useState<{ schemaName: string, tableName: string } | null>(null);

  useEffect(() => {
    if (selectedVariable?.isLookup && selectedVariable.lookupTableId) {
      getReferenceTableById(selectedVariable.lookupTableId)
        .then((data) => setLookupTableInfo({schemaName: data.schemaName, tableName: data.tableName}) )
        .catch(error => {
          handleError(error);
          setLookupTableInfo(null);
        });
    } else {
      setLookupTableInfo(null);
    }
  }, [selectedVariable, methods]);
  
  const { data: lookupOptions = [] } = useReferenceData(
    lookupTableInfo?.schemaName as string,
    lookupTableInfo?.tableName as string
  );

  const handleFormSubmit = async (values: DepthMeasurementRecordFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="measuredAt"
                control={methods.control}
                render={({ field }) => {
                  const value = field.value
                    ? dayjs(field.value, "YYYY-MM-DD HH:mm:ss")
                    : null;

                  return (
                    <DateTimePicker
                      label={t("captures.capture.depth-measurement.measured-at")}
                      value={value}
                      format="DD/MM/YYYY, HH:mm"
                      onChange={(date) => {
                        field.onChange(
                          date ? date.format("YYYY-MM-DD HH:mm:ss") : null
                        );
                      }}
                      disabled={!isEditing || isSaving || isDeleting}
                      sx={{ ...formStyles }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: isEditing ? "outlined" : "standard",
                          error: !!methods.formState.errors.measuredAt,
                          helperText: methods.formState.errors.measuredAt?.message,
                        }
                      }}
                    />
                  );
                }}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2 size={4}>
            <TextField
              fullWidth
              label={`${t("captures.capture.depth-measurement.start-depth")} *`}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register(
                "startDepth",
                { valueAsNumber: true }
              )}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.startDepth}
              helperText={methods.formState.errors.startDepth?.message}
            />
          </Grid2>

          <Grid2 size={4}>
            <TextField
              fullWidth
              label={`${t("captures.capture.depth-measurement.end-depth")} *`}                         
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register(
                "endDepth",
                { valueAsNumber: true }
              )}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                  ),
                },
              }}
              error={!!methods.formState.errors.endDepth}
              helperText={methods.formState.errors.endDepth?.message}
            />
          </Grid2>
    
          <Grid2 size={4}>
            <FormAutocomplete
              name="variableId" 
              label={`${t("captures.capture.depth-measurement.variable")} *`} 
              options={variables}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>

          {isEditing ? (
            <>
              <Grid2 size={2}>
                <TextField
                  fullWidth
                  label={t("captures.capture.depth-measurement.flag")}
                  variant="outlined"
                  disabled={isSaving}
                  sx={{ ...formStyles }}
                  {...methods.register("flag")}
                  error={!!methods.formState.errors.flag}
                  helperText={methods.formState.errors.flag?.message}
                />
              </Grid2>

              <Grid2 size={4}>
                {selectedVariable?.isLookup ? (
                  <FormAutocomplete
                    name="value"
                    label={t("captures.capture.depth-measurement.value")}
                    options={lookupOptions}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    disabled={!isEditing || isSaving || !variableId}
                    formStyles={formStyles}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={`${t("captures.capture.depth-measurement.value")} *`}                       
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || !variableId}
                    sx={{ ...formStyles }}
                    {...methods.register(
                      "value",
                      { valueAsNumber: true }
                    )}
                    error={!!methods.formState.errors.value}
                    helperText={methods.formState.errors.value?.message}
                  />
                )}
              </Grid2>
            </>
          ) : (
            <Grid2 size={4}>
              <TextField
                label={`${t("captures.capture.depth-measurement.value")} *`}                       
                variant="standard"
                disabled 
                sx={{ 
                  ...formStyles, 
                  width: `${Math.max(50, (String(methods.getValues("value") ?? "").length + (selectedVariable?.unit?.length ?? 0) + 2) * 10)}px`
                }}
                {...methods.register("value", { valueAsNumber: true })}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">{methods.watch("flag")}</InputAdornment>,
                    endAdornment: <InputAdornment position="end">{selectedVariable?.unit}</InputAdornment>
                  },
                  inputLabel: { shrink: true }
                }}
              />
            </Grid2>
          )}
        </Grid2>
      </Box>
    </FormProvider>
  );
}